import { useState, useEffect } from 'react';
import { AlertTriangle, User, Route, Users, Loader2, XCircle, CheckCircle, Ban } from 'lucide-react';
import unifiedApi from '../services/unifiedApi';
import logger from '@/utils/logger';

const ENTITY_CONFIG = {
  driver: {
    label: 'chauffeur',
    apiCall: (id) => unifiedApi.drivers.previewDelete(id),
  },
  bus: {
    label: 'bus',
    apiCall: (id) => unifiedApi.buses.previewDelete(id),
  },
  parent: {
    label: 'parent',
    apiCall: (id) => unifiedApi.parents.previewDelete(id),
  },
  stop: {
    label: "l'arrêt",
    apiCall: (id) => unifiedApi.stops.previewDelete(id),
  },
};

const DeleteImpactModal = ({ entity, entityType, entityName, onConfirm, onCancel }) => {
  const [impacts, setImpacts] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const config = ENTITY_CONFIG[entityType];

  useEffect(() => {
    if (!entity?.id || !config) return;
    setLoading(true);
    setError(null);
    config
      .apiCall(entity.id)
      .then((response) => {
        if (response.success) {
          setImpacts(response.impacts);
        } else {
          throw new Error(response.error || 'Erreur lors du chargement des impacts');
        }
      })
      .catch((err) => {
        logger.error('Erreur chargement impacts suppression:', err);
        setError(err.message);
      })
      .finally(() => setLoading(false));
  }, [entity?.id, entityType]);

  const isBlocked = impacts?.isBlocked;

  const renderDriverImpacts = () => (
    <div className="space-y-2">
      {impacts.allRoutes?.length > 0 ? (
        <div className="flex items-start text-sm text-red-700">
          <Route className="w-4 h-4 mr-2 mt-0.5 flex-shrink-0" />
          <span>
            <strong>{impacts.activeRoutes.length} circuit(s) actif(s)</strong> empêchent la suppression&nbsp;:
            <ul className="mt-1 ml-4 list-disc text-xs">
              {impacts.activeRoutes.map((r) => (
                <li key={r.id}>{r.name || r.code}</li>
              ))}
            </ul>
            {impacts.allRoutes.length > impacts.activeRoutes.length && (
              <span className="block mt-1 text-xs text-red-500">
                {impacts.allRoutes.length - impacts.activeRoutes.length} autre(s) circuit(s) inactif(s) associé(s)
              </span>
            )}
          </span>
        </div>
      ) : (
        <div className="flex items-center text-sm text-gray-600">
          <CheckCircle className="w-4 h-4 mr-2 text-green-500" />
          <span>Aucun circuit actif associé — suppression possible</span>
        </div>
      )}
    </div>
  );

  const renderBusImpacts = () => (
    <div className="space-y-2">
      {impacts.assignedDriver && (
        <div className="flex items-start text-sm text-orange-700">
          <User className="w-4 h-4 mr-2 mt-0.5 flex-shrink-0" />
          <span>
            Chauffeur assigné&nbsp;: <strong>{impacts.assignedDriver.name}</strong> (sera désassigné)
          </span>
        </div>
      )}
      {impacts.activeRoutes?.length > 0 ? (
        <div className="flex items-start text-sm text-red-700">
          <Route className="w-4 h-4 mr-2 mt-0.5 flex-shrink-0" />
          <span>
            <strong>{impacts.activeRoutes.length} circuit(s) actif(s)</strong> empêchent la suppression&nbsp;:
            <ul className="mt-1 ml-4 list-disc text-xs">
              {impacts.activeRoutes.map((r) => (
                <li key={r.id}>{r.name || r.code}</li>
              ))}
            </ul>
          </span>
        </div>
      ) : impacts.allRoutes?.length === 0 && !impacts.assignedDriver ? (
        <div className="flex items-center text-sm text-gray-600">
          <CheckCircle className="w-4 h-4 mr-2 text-green-500" />
          <span>Aucun impact — suppression possible</span>
        </div>
      ) : null}
    </div>
  );

  const renderParentImpacts = () => (
    <div className="space-y-2">
      {impacts.children?.length > 0 ? (
        <div className="flex items-start text-sm text-red-700">
          <Users className="w-4 h-4 mr-2 mt-0.5 flex-shrink-0" />
          <span>
            <strong>{impacts.children.length} élève(s) associé(s)</strong> empêchent la suppression&nbsp;:
            <ul className="mt-1 ml-4 list-disc text-xs">
              {impacts.children.slice(0, 5).map((c) => (
                <li key={c.id}>
                  {c.name} <span className="text-gray-500">({c.status === 'active' ? 'actif' : 'inactif'})</span>
                </li>
              ))}
              {impacts.children.length > 5 && <li>+{impacts.children.length - 5} autre(s)</li>}
            </ul>
          </span>
        </div>
      ) : (
        <div className="flex items-center text-sm text-gray-600">
          <CheckCircle className="w-4 h-4 mr-2 text-green-500" />
          <span>Aucun enfant associé — suppression possible</span>
        </div>
      )}
    </div>
  );

  const renderStopImpacts = () => (
    <div className="space-y-2">
      {impacts.activeStudents?.length > 0 && (
        <div className="flex items-start text-sm text-red-700">
          <Users className="w-4 h-4 mr-2 mt-0.5 flex-shrink-0" />
          <span>
            <strong>{impacts.activeStudents.length} élève(s) actif(s)</strong> assigné(s) à cet arrêt (bloquant)&nbsp;:
            <ul className="mt-1 ml-4 list-disc text-xs">
              {impacts.activeStudents.slice(0, 5).map((s) => (
                <li key={s.id}>{s.name}</li>
              ))}
              {impacts.activeStudents.length > 5 && <li>+{impacts.activeStudents.length - 5} autre(s)</li>}
            </ul>
          </span>
        </div>
      )}
      {impacts.allStudents?.length > impacts.activeStudents?.length && (
        <div className="flex items-start text-sm text-orange-700">
          <Users className="w-4 h-4 mr-2 mt-0.5 flex-shrink-0" />
          <span>
            {impacts.allStudents.length - impacts.activeStudents.length} élève(s) inactif(s) auront leur arrêt effacé
          </span>
        </div>
      )}
      {impacts.activeRoutes?.length > 0 && (
        <div className="flex items-start text-sm text-red-700">
          <Route className="w-4 h-4 mr-2 mt-0.5 flex-shrink-0" />
          <span>
            <strong>{impacts.activeRoutes.length} circuit(s) actif(s)</strong> passent par cet arrêt (bloquant)&nbsp;:
            <ul className="mt-1 ml-4 list-disc text-xs">
              {impacts.activeRoutes.map((r) => (
                <li key={r.id}>{r.name || r.code}</li>
              ))}
            </ul>
          </span>
        </div>
      )}
      {impacts.affectedRoutes?.length > impacts.activeRoutes?.length && (
        <div className="flex items-start text-sm text-orange-700">
          <Route className="w-4 h-4 mr-2 mt-0.5 flex-shrink-0" />
          <span>
            {impacts.affectedRoutes.length - impacts.activeRoutes.length} circuit(s) inactif(s) auront cet arrêt retiré
          </span>
        </div>
      )}
      {impacts.allStudents?.length === 0 && impacts.affectedRoutes?.length === 0 && (
        <div className="flex items-center text-sm text-gray-600">
          <CheckCircle className="w-4 h-4 mr-2 text-green-500" />
          <span>Aucun impact — suppression possible</span>
        </div>
      )}
    </div>
  );

  const renderImpactBody = () => {
    if (loading) {
      return (
        <div className="flex items-center justify-center py-8">
          <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
          <span className="ml-2 text-sm text-gray-600">Analyse des impacts en cours...</span>
        </div>
      );
    }
    if (error) {
      return (
        <div className="flex items-center justify-center py-8 text-red-600">
          <XCircle className="w-5 h-5 mr-2" />
          <span className="text-sm">Erreur&nbsp;: {error}</span>
        </div>
      );
    }
    if (!impacts) return null;

    return (
      <div className={`rounded-lg p-4 mb-6 space-y-3 ${isBlocked ? 'bg-red-50' : 'bg-orange-50'}`}>
        <p className={`text-sm font-medium mb-2 ${isBlocked ? 'text-red-800' : 'text-orange-800'}`}>
          {isBlocked ? 'Suppression impossible :' : 'Conséquences de la suppression :'}
        </p>
        {entityType === 'driver' && renderDriverImpacts()}
        {entityType === 'bus' && renderBusImpacts()}
        {entityType === 'parent' && renderParentImpacts()}
        {entityType === 'stop' && renderStopImpacts()}
      </div>
    );
  };

  const label = config?.label ?? 'entité';
  const name = entityName ?? entity?.name ?? '';

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-md w-full p-6 border-l-4 border-red-500 animate-in fade-in zoom-in duration-200">
        <div className="flex items-start mb-4">
          <div className="p-2 bg-red-100 rounded-full mr-3">
            <AlertTriangle className="w-6 h-6 text-red-600" />
          </div>
          <div>
            <h3 className="text-lg font-bold text-gray-900">Confirmation de suppression</h3>
            <p className="text-sm text-gray-600 mt-1">
              Vous êtes sur le point de supprimer {label} <strong>{name}</strong>.
            </p>
          </div>
        </div>

        {renderImpactBody()}

        <div className="flex space-x-3">
          <button
            onClick={onCancel}
            className="flex-1 px-4 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 transition-colors font-medium"
            disabled={loading}
          >
            Annuler
          </button>
          {isBlocked ? (
            <button
              disabled
              className="flex-1 px-4 py-2 bg-gray-300 text-gray-500 rounded-lg font-medium cursor-not-allowed flex items-center justify-center gap-2"
            >
              <Ban className="w-4 h-4" />
              Impossible
            </button>
          ) : (
            <button
              onClick={onConfirm}
              disabled={loading || !!error}
              className="flex-1 px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Chargement...' : 'Supprimer'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default DeleteImpactModal;
